import { ToastUtilities } from "@src/libs/react-toastify/ToastUtilities";
import { LocalData } from "@src/to-be-removed/LocalData/General/LocalData";
import { PropertyData } from "@src/to-be-removed/LocalData/Property/PropertyData";
import { RoutineData } from "@src/to-be-removed/LocalData/Routines/RoutineData";
import { DataCreator } from "@src/utils/dataCreator";
import { t } from "i18next";
import { useMemo, useState } from "react";
import { Checkbox } from "../../ui-components/Checkbox/Checkbox";
import { ModalPopup } from "../ModalPopup/ModalPopup";
import { Popup } from "../Popup/Popup";
import { PropertySelector } from "../PropertySelector/PropertySelector";
import { Text } from "../Text/Text";
import { TextInputField } from "../TextInputField/TextInputField";

type Props = {
  id: string;
  routines: RoutineData[];
  didDuplicate: (routines: RoutineData[]) => void;
};

export const RoutineDuplicationPopup = (props: Props) => {
  const [title, setTitle] = useState(props.routines[0].title);
  const [properties, setProperties] = useState<PropertyData[] | undefined>([]);

  // Default values for modal
  const modalTitle = useMemo(
    () => (props.routines.length === 1 ? t("routine.actions.duplicate.singleTitle") : t("routine.actions.duplicate.multiTitle")),
    [props.routines.length]
  );
  const modalDescription = useMemo(
    () => (props.routines.length === 1 ? t("routine.actions.duplicate.singleDescription") : t("routine.actions.duplicate.multiDescription")),
    [props.routines.length]
  );
  const modalPropertiesDescription = useMemo(
    () =>
      props.routines.length === 1
        ? t("routine.actions.duplicate.singleCustomPropertiesDescription")
        : t("routine.actions.duplicate.multiCustomPropertiesDescription"),
    [props.routines.length]
  );
  const propertiesOptions = useMemo(
    () => LocalData.currentUser.properties.filter((p) => !p.deleted && p.checkForLandlordRoles(LocalData.currentUser)),
    [LocalData.currentUser]
  );

  const formValuesValid = title.length > 0 && (properties === undefined || properties.length > 0);

  const onConfirm = () => {
    const result: RoutineData[] = [];
    const promises: Promise<any>[] = [];

    for (const routine of props.routines) {
      if (properties) {
        const name = props.routines.length === 1 ? title : routine.title;
        for (const property of properties) {
          promises.push(
            new Promise((resolve) =>
              DataCreator.duplicateRoutine(routine, name, [property.id], (routine) => {
                result.push(routine);
                resolve([routine]);
              })
            )
          );
        }
      } else {
        const name = props.routines.length === 1 ? title : `${routine.title} (${t("routine.actions.duplicate.copy")})`;
        promises.push(
          new Promise((resolve) =>
            DataCreator.duplicateRoutine(routine, name, undefined, (routine) => {
              result.push(routine);
              resolve([routine]);
            })
          )
        );
      }
    }

    ToastUtilities.presentSavingToast(Promise.all<RoutineData>(promises)).then(() => {
      Popup.remove(props.id);
      props.didDuplicate(result);
    });
  };

  return (
    <ModalPopup
      id={props.id}
      title={modalTitle}
      disableAction={!formValuesValid}
      onActionClicked={() => onConfirm()}
      containerProps={{ maxWidth: "800px" }}
      children={
        <div>
          <Text color="secondary" marginBottom={32}>
            {modalDescription}
          </Text>
          {props.routines.length === 1 && (
            <>
              <Text marginBottom={8} isBold>
                {t("main.title")}
              </Text>
              <TextInputField value={title} onChange={setTitle} fullWidth marginBottom={16} />
            </>
          )}
          <Text marginBottom={8} isBold>
            {t("main.properties")}
          </Text>

          <PropertySelector
            isMulti
            changedSelectedProperties={setProperties}
            optionsSelected={properties}
            properties={propertiesOptions}
            disabled={properties === undefined}
          />
          <Checkbox
            value={properties === undefined}
            onChange={(value) => setProperties(value ? undefined : [])}
            description={modalPropertiesDescription}
            marginTop={8}
          />
        </div>
      }
    />
  );
};
